import { AlertTriangle, X } from 'lucide-react';
import type { ActiveFlag } from '@hcc/types';
import { formatDateTime } from '../../lib/dateFmt';

/**
 * Warning banner shown above any person card or station drawer when
 * the person has at least one active flag. Flags are visible on
 * every station screen until cleared, but they never gate any
 * action — they're a reminder, not a stop sign.
 *
 * Visuals: soft-warning surface with a 2px accent border-left strip,
 * matching the Aurora flag treatment from the preview mocks.
 */
export function FlagBanner({
  flags,
  onClearClick,
}: {
  flags: readonly ActiveFlag[];
  onClearClick?: (f: ActiveFlag) => void;
}) {
  if (flags.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-token-lg border border-warning/30 bg-warning-soft/10 shadow-token-sm">
      <div className="flex items-center gap-2 border-b border-warning/20 bg-warning-soft/15 px-4 py-2.5 text-sm font-semibold text-warning-soft-fg">
        <AlertTriangle className="h-4 w-4" strokeWidth={2.5} />
        <span>
          {flags.length} active flag{flags.length === 1 ? '' : 's'}
        </span>
      </div>
      <ul className="divide-y divide-warning/15">
        {flags.map((f) => (
          <li
            key={f.event_id}
            className="flex items-start justify-between gap-3 px-4 py-3"
          >
            <div className="min-w-0 flex-1">
              <span className="text-sm font-medium text-warning-soft-fg">
                {f.reason}
              </span>
              <div className="mt-1 text-xs text-warning-soft-fg/80">
                Raised {formatDateTime(f.flagged_at)}
              </div>
            </div>
            {onClearClick ? (
              <button
                type="button"
                onClick={() => onClearClick(f)}
                className="inline-flex shrink-0 items-center gap-1 rounded-token-sm border border-warning/30 bg-surface px-2.5 py-1 text-xs font-medium text-warning-soft-fg transition-colors hover:bg-warning-soft/20"
              >
                <X className="h-3 w-3" />
                Clear
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
